import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { postOrder } from "../../app/services/orderService";
import { useAppDispatch } from "../../app/hooks";
import { useAccount, useMsal } from "@azure/msal-react";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import Form from "react-bootstrap/Form";
import {
  Alert,
  Breadcrumb,
  Button,
  Col,
  Container,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { defaultOrder, getDefaultOrder, Order } from "../../models/responses/order";
import { OrderStatus } from "../../models/responses/orderStatus";
import {
  getOrderStatusById,
  getOrderStatuses,
} from "../../app/services/orderStatusService";
import { Customer } from "../../models/responses/customer";
import { getCustomers } from "../../app/services/customerService";
import { OrderType } from "../../models/responses/orderType";
import { getOrderTypes } from "../../app/services/orderTypeService";
import { Supplier } from "../../models/responses/supplier";
import { getSuppliers } from "../../app/services/supplierService";
import { Article, defaultArticle } from "../../models/responses/article";
import {
  Check,
  CheckAll,
  ExclamationTriangle,
  Pen,
  Plus,
  X,
} from "react-bootstrap-icons";
import { ClientType } from "../../models/enums/clientTypeEnum";
import {
  calculateFromPercentage,
  calculateStoreSalesPriceFromSalesPrice,
  calculateWorkshopPriceFromSalesPrice,
} from "../../app/services/calculationsHelperService";
import { getCarBrands } from "../../app/services/carBrandService";
import { getBrandDiscountByCarBrandId } from "../../app/services/brandDiscountService";
import { setClientToggleLocked } from "../../app/slices/clientTypeSlice";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import { getArticleTemplatesByType } from "../../app/services/articleTemplateService";

interface Props { }

const OrderAdd: FC<Props> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [displayError, setDisplayError] = useState(false);
  const { orderStatusId } = useParams<{ orderStatusId: string }>();
  const clientType = useSelector(
    (state: RootState) => state.clientType.clientType
  );
  const [orderData, setOrderData] = useState<Order>({
    ...getDefaultOrder(clientType),
    customerId: "",
  });
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [modalArticle, setModalArticle] = useState<Article>({
    ...defaultArticle,
  });

  const [allVerified, setAllVerified] = useState(false);
  const [showAlreadyExistsModal, setShowAlreadyExistsModal] = useState(false);
  const [pendingOverrideOrder, setPendingOverrideOrder] = useState<Order | null>(null);
  const [alreadyExistsMessage, setAlreadyExistsMessage] = useState<string>("");

  const orderStatus: OrderStatus = useSelector(
    (state: RootState) => state.orderStatus.orderStatus
  );
  const orderStatusList: OrderStatus[] = useSelector(
    (state: RootState) => state.orderStatus.setOrderStatusList
  );
  const customers: Customer[] = useSelector(
    (state: RootState) => state.customer.customerList
  );
  const orderTypes: OrderType[] = useSelector(
    (state: RootState) => state.orderTypes.orderTypeList
  );
  const suppliers: Supplier[] = useSelector(
    (state: RootState) => state.supplier.supplierList
  );
  const isWaitingOrderPostResponse = useSelector(
    (state: RootState) => state.order.isWaitingOrderPostResponse
  );
  const isLoadingCarBrands = useSelector(
    (state: RootState) => state.carBrand.isWaitingCarBrandGetResponse
  );
  const carBrands = useSelector(
    (state: RootState) => state.carBrand.carBrandList
  );
  const brandDiscount = useSelector(
    (state: RootState) => state.brandDiscount.brandDiscount
  );
  const articleTemplateListTime = useSelector((state: RootState) => state.articleTemplate.articleTemplateListTime);
  const articleTemplateListProduct = useSelector((state: RootState) => state.articleTemplate.articleTemplateListProduct);

  useEffect(() => {
    dispatch(setClientToggleLocked(true));
    dispatch(getCarBrands(instance, account));
  }, [instance, account, dispatch]);

  useEffect(() => {
    dispatch(
      getBrandDiscountByCarBrandId(
        instance,
        account,
        orderData?.selectedCarBrandId || ""
      )
    );
  }, [dispatch, instance, account, orderData?.selectedCarBrandId]);

  useEffect(() => {
    orderData.articles?.forEach((article) => {
      articleListSalesPriceHandler(article.rowNumber, article.salesPrice);
      articleSalesPriceInputHandler({
        target: { value: article.salesPrice },
      } as React.ChangeEvent<HTMLInputElement>);
    });
  }, [brandDiscount]);

  useEffect(() => {
    dispatch(getOrderStatusById(instance, account, orderStatusId));
  }, [account, accounts, dispatch, instance, orderStatusId]);

  useEffect(() => {
    dispatch(getOrderStatuses(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getCustomers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    if (orderStatusList.length > 0) {
      orderData.orderStatusId = orderStatusList[0].id;
    }
  }, [orderStatusList]);

  useEffect(() => {
    if (orderTypes.length > 0) {
      setOrderData((prev) => ({
        ...prev,
        orderTypeId: orderTypes[0].id,
        discount: orderTypes[0].discount ?? null,
      }));
    }
  }, [orderTypes]);

  useEffect(() => {
    dispatch(getOrderTypes(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    dispatch(getSuppliers(instance, account, clientType));
  }, [account, accounts, dispatch, instance, clientType]);

  useEffect(() => {
    checkVerified();
  }, [orderData.articles]);

  useEffect(() => {
    setModalArticle({
      ...modalArticle,
      totalExVat: parseFloat(
        (parseFloat(modalArticle.salesPrice) * modalArticle.quantity).toFixed(2)
      ),
    });
  }, [modalArticle.quantity, modalArticle.salesPrice, modalArticle.vat]);

  useEffect(() => {
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Time));
    dispatch(getArticleTemplatesByType(instance, account, ArticleType.Product));
  }, [account, dispatch, instance]);

  const customerInputHandler = (e: string) => {
    setOrderData({ ...orderData, customerId: e });
  };
  const assignedToInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, assignedTo: e.target.value });
  };
  const registrationNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, registrationNumber: e.target.value });
  };
  const vinInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, vin: e.target.value });
  };
  const carInfoInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, carInfo: e.target.value });
  };
  const aoNumberInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, aoNumber: e.target.value });
  };
  const orderTypeInputHandler = (e: string) => {
    const selectedType = orderTypes.find((type) => type.id === e);
    const newDiscount = selectedType?.discount ?? 0;
    setOrderData({
      ...orderData,
      orderTypeId: e,
      discount: newDiscount,
      articles:
        orderData.articles?.map((article) => ({
          ...article,
          discount: newDiscount,
        })) ?? [],
    });
  };
  const orderDiscountInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const input = e.target.value;
    if (input === "") {
      setOrderData({
        ...orderData,
        discount: null,
        articles:
          orderData.articles?.map((article) =>
            article.verified ? article : { ...article, discount: null }
          ) ?? [],
      });
    } else {
      const value = Number(input);
      if (value >= 0 && value <= 100) {
        setOrderData({
          ...orderData,
          discount: value,
          articles:
            orderData.articles?.map((article) =>
              article.verified ? article : { ...article, discount: value }
            ) ?? [],
        });
      }
    }
  };
  const emailBodyInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, emailBody: e.target.value });
  };
  const arriveDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, arriveDate: e.target.value });
  };
  const bookingDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, bookingDate: e.target.value });
  };
  const deliveryDateInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, deliveryDate: e.target.value });
  };
  const orderFilenameInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, filename: e.target.value });
  };
  const commentInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, comment: e.target.value });
  };
  const internalCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, internalComment: e.target.value });
  };
  const priceProposalMessageInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, priceProposalMessage: e.target.value });
  };
  const orderApprovedInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, approved: e.target.checked });
  };
  const orderDeniedInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setOrderData({ ...orderData, denied: e.target.checked });
  };
  const orderStatusInputHandler = (e: string) => {
    setOrderData({ ...orderData, orderStatusId: e });
  };
  const clientExternalOrderIdInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, clientExternalOrderId: e.target.value });
  };
  const workshopIdInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, workshopId: e.target.value });
  };
  const workshopNameInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, workshopName: e.target.value });
  };
  const odometerInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, odometer: Number(e.target.value) });
  };
  const clientVehicleRetrieverInputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrderData({ ...orderData, clientVehicleRetriever: e.target.value });
  };
  const selectedCarBrandInputHandler = (e: string) => {
    if (e === "") {
      setOrderData({ ...orderData, selectedCarBrandId: null });
    } else {
      const selectedBrand = carBrands.find((brand) => brand.id === e);
      setOrderData({
        ...orderData,
        selectedCarBrandId: selectedBrand ? selectedBrand.id : null,
      });
    }
  };

  // ARTICLES // -----------------------------
  const articleListQuantityHandler = (
    rowNumber: number,
    newQuantity: number
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, quantity: newQuantity }
          : article
      ),
    });
  };
  const articleListDescriptionInputHandler = (
    rowNumber: number,
    newDescription: string
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, description: newDescription }
          : article
      ),
    });
  };
  const articleListArticleNumberHandler = (
    rowNumber: number,
    newArticleNumber: string
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, articleNumber: newArticleNumber }
          : article
      ),
    });
  };
  const articleListSalesPriceHandler = (
    rowNumber: number,
    newSalesPrice: string
  ) => {
    let value = newSalesPrice.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (isNaN(parseFloat(value))) value = "0";
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            salesPrice: value,
            storeSalesPrice: calculateStoreSalesPriceFromSalesPrice(
              value,
              brandDiscount
            ).toString(),
            workshopSalesPrice: calculateWorkshopPriceFromSalesPrice(
              value,
              brandDiscount
            ).toString(),
          }
          : article
      ),
    });
  };
  const articleListDiscountHandler = (
    rowNumber: number,
    newDiscount: string
  ) => {
    if (newDiscount === "") {
      setOrderData({
        ...orderData,
        articles: orderData.articles!.map((article) =>
          article.rowNumber === rowNumber
            ? { ...article, discount: null }
            : article
        ),
      });
      return;
    }
    let num = Number(newDiscount);
    if (isNaN(num)) return;
    if (num < 0 || num > 100) return;
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, discount: num }
          : article
      ),
    });
  };
  const articleArticleNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, articleNumber: e.target.value });
  };
  const articleQuantityInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, quantity: parseInt(e.target.value) });
  };
  const articleDeliveredQuantityInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({
      ...modalArticle,
      deliveredQuantity: parseInt(e.target.value),
    });
  };
  const articleExternalArticleCodeInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, externalArticleCode: e.target.value });
  };
  const articleDiscountInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === "") {
      setModalArticle({ ...modalArticle, discount: null });
      return;
    }
    const num = Number(value);
    if (isNaN(num)) return;
    if (num < 0 || num > 100) return;
    setModalArticle({ ...modalArticle, discount: num });
  };
  const articleDescriptionInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, description: e.target.value });
  };
  const articleSalesOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, salesOrderNumber: e.target.value });
  };
  const articlePurchaseOrderNumberInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, purchaseOrderNumber: e.target.value });
  };
  const articleInternalCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, internalComment: e.target.value });
  };
  const articleCommentInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, comment: e.target.value });
  };
  const articlePurchasePriceInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (value.includes("%")) {
      value = calculateFromPercentage(
        modalArticle.salesPrice,
        value
      ).toString();
    }
    if (isNaN(parseFloat(value))) value = "0";
    setModalArticle({
      ...modalArticle,
      purchasePrice: value,
    });
  };
  const articleSalesPriceInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value.replace(",", ".");
    if (value.startsWith("0") && !value.includes("."))
      value = value.substring(1);
    if (isNaN(parseFloat(value))) value = "0";
    setModalArticle({
      ...modalArticle,
      salesPrice: value,
      storeSalesPrice: calculateStoreSalesPriceFromSalesPrice(
        value,
        brandDiscount
      ).toString(),
      workshopSalesPrice: calculateWorkshopPriceFromSalesPrice(
        value,
        brandDiscount
      ).toString(),
    });
  };
  const articleSupplierInputHandler = (e: string | null) => {
    if (e === "") e = null;

    setModalArticle({
      ...modalArticle,
      supplierId: e,
      supplier: suppliers.find((s) => s.id === e) || null,
    });
  };
  const articleReviewedQuoteInputHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setModalArticle({ ...modalArticle, reviewedQuote: e.target.checked });
  };
  const articleListTimeHandler = (
    rowNumber: number,
    newTime: number
  ) => {
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? { ...article, time: newTime }
          : article
      ),
    });
  };
  const saveArticleInModalHandler = () => {
    const savedArticles = orderData.articles?.map<Article>((a) => {
      if (a.rowNumber === modalArticle.rowNumber) return modalArticle;
      return a;
    });

    if (savedArticles) {
      setOrderData({ ...orderData, articles: savedArticles });
    }

    setShowArticleModal(false);
  };

  const validatedHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setModalArticle({ ...modalArticle, verified: e.target.checked });
  };

  const removeArticleHandler = () => {
    const keptArticles = orderData.articles?.filter((a) => {
      return a.rowNumber !== modalArticle.rowNumber;
    });

    if (keptArticles) {
      setOrderData({ ...orderData, articles: keptArticles });
    }

    setShowArticleModal(false);
  };

  const addNewArticleHandler = () => {
    let newArticle: Article = JSON.parse(JSON.stringify(defaultArticle));
    newArticle.rowNumber = calculateRowNumber();
    const selectedOrderType = orderTypes.find(
      (type) => type.id === orderData.orderTypeId
    );
    newArticle.discount = selectedOrderType?.discount ?? 0;

    let articlesList = Array.isArray(orderData.articles)
      ? orderData.articles
      : [];
    let newList = [...articlesList, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const addNewTimeArticleHandler = () => {
    let newArticle: Article = JSON.parse(JSON.stringify(defaultArticle));
    newArticle.rowNumber = calculateRowNumber();
    newArticle.articleType = ArticleType.Time;

    let articlesList = Array.isArray(orderData.articles)
      ? orderData.articles
      : [];
    let newList = [...articlesList, newArticle];

    setOrderData({
      ...orderData,
      articles: newList,
    });

    setModalArticle(newArticle);
  };

  const calculateRowNumber = () => {
    if (!orderData.articles || orderData.articles.length === 0) return 1;
    return Math.max(...orderData.articles.map((a) => a.rowNumber)) + 1;
  };

  const articleTemplateHandler = (rowNumber: number, templateId: string, type: ArticleType) => {
    const list = type === ArticleType.Time ? articleTemplateListTime : articleTemplateListProduct;
    const template = list.find(t => t.id === templateId);
    if (!template) return;
    setOrderData({
      ...orderData,
      articles: orderData.articles!.map((article) =>
        article.rowNumber === rowNumber
          ? {
            ...article,
            salesPrice: template.keepIncomingPrice
              ? article.salesPrice
              : template.salesPrice,
            articleNumber: template.articleNumber,
          }
          : article
      ),
    });
  };
  // ARTICLES END // -----------------------------

  const checkVerified = () => {
    let allVerified = orderData.articles?.every((art) => {
      if (art.verified) return true;
      return false;
    });

    if (allVerified) {
      setAllVerified(true);
    } else {
      setAllVerified(false);
    }
  };


  const handleAddOrder = () => {
    setDisplayError(false);
    if (!orderData.customerId) {
      setDisplayError(true);
      return;
    }
    dispatch(postOrder(instance, account, orderData, onSuccess,
      (errorType, errorMessage) => {
        if (errorType === "ALREADY_EXISTS") {
          setAlreadyExistsMessage(errorMessage || "Det finns redan en order med detta PostNord-id i systemet.");
          setPendingOverrideOrder({ ...orderData });
          setShowAlreadyExistsModal(true);
        } else {
          setDisplayError(true);
        }
      }
    ));
  };

  const handleOverrideOrder = () => {
    if (pendingOverrideOrder) {
      const overrideOrder = { ...pendingOverrideOrder, overrideException: true };
      setShowAlreadyExistsModal(false);
      setPendingOverrideOrder(null);
      dispatch(postOrder(instance, account, overrideOrder, onSuccess,
        (errorType, errorMessage) => {
          setDisplayError(true);
        }
      ));
    }
  };

  const handleCancelOverride = () => {
    setShowAlreadyExistsModal(false);
    setPendingOverrideOrder(null);
  };

  const openArticleModalHandler = (article: Article) => {
    setModalArticle({ ...modalArticle, ...article });
    setShowArticleModal(true);
  };

  const closeArticleModalHandler = () => {
    setShowArticleModal(false);
  };

  const onSuccess = (orderId: string | null) => {
    if (orderId === null) {
      setDisplayError(true);
      return;
    }

    setTimeout(() => {
      navigate(`../view/${orderId}`, { relative: "path" });
    }, 500);
  };

  return (
    <div>
      <Modal show={showAlreadyExistsModal} onHide={handleCancelOverride} backdrop="static" centered>
        <Modal.Header closeButton>
          <Modal.Title>Varning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{alreadyExistsMessage || "Det finns redan en order med detta PostNord-id i systemet. Vill du skapa den ändå?"}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelOverride}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleOverrideOrder}>
            Skapa ändå
          </Button>
        </Modal.Footer>
      </Modal>
      <Breadcrumb>
        <Breadcrumb.Item href={`/orderStatus/${orderStatus.id}`}>
          {orderStatus.name}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Ny order</Breadcrumb.Item>
      </Breadcrumb>
      <hr />
      <Form>
        <Row>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                aria-label="Status"
                value={orderData.orderStatusId || ""}
                onChange={(e) => orderStatusInputHandler(e.target.value)}
              >
                {orderStatusList.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={0} md={6}></Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Kund</Form.Label>
              <Form.Select
                aria-label="Kund"
                value={orderData.customerId || ""}
                onChange={(e) => customerInputHandler(e.target.value)}
              >
                <option value="">Välj kund</option> {/* Ny default option */}
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Ansvarig</Form.Label>
              <Form.Control
                type="text"
                value={orderData.assignedTo ?? ""}
                onInput={assignedToInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={2}>
            <Form.Group className="mb-3">
              <Form.Label>Registreringsnummer *</Form.Label>
              <Form.Control
                type="text"
                value={orderData.registrationNumber ?? ""}
                onInput={registrationNumberInputHandler}
              />
            </Form.Group>
          </Col>
          {clientType === ClientType.Catalog && (
            <Col xs={12} md={2}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Bilmärke{" "}
                  {isLoadingCarBrands && (
                    <div className="ms-3 d-inline-block">
                      <Spinner variant="primary" size="sm" />
                    </div>
                  )}
                </Form.Label>
                <Form.Select
                  aria-label="Bilmärke"
                  value={orderData.selectedCarBrandId || ""}
                  onChange={(e) => selectedCarBrandInputHandler(e.target.value)}
                >
                  <option key={0} value="">
                    - Inget bilmärke valt -
                  </option>
                  {carBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>VIN</Form.Label>
              <Form.Control
                type="text"
                value={orderData.vin ?? ""}
                onInput={vinInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Bilmärke</Form.Label>
              <Form.Control
                type="text"
                value={orderData.carInfo ?? ""}
                onInput={carInfoInputHandler}
              />
            </Form.Group>
          </Col>
          {clientType === ClientType.Standard && (
            <>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>AO-nummer</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.aoNumber ?? ""}
                    onInput={aoNumberInputHandler}
                  />
                </Form.Group>
              </Col>
            </>
          )}
          <Col xs={12} md={2}>
            <Form.Group className="mb-3">
              <Form.Label>Ordertyp</Form.Label>
              <Form.Select
                aria-label="Ordertyp"
                value={orderData.orderTypeId || ""}
                onChange={(e) => orderTypeInputHandler(e.target.value)}
              >
                {orderTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          {clientType === ClientType.Standard && (
            <Col xs={12} md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Rabatt (%)</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  max={100}
                  value={orderData.discount === null ? "" : orderData.discount}
                  onChange={orderDiscountInputHandler}
                />
              </Form.Group>
            </Col>
          )}
          {clientType === ClientType.Standard && (
            <>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>E-post innehåll</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.emailBody ?? ""}
                    onInput={emailBodyInputHandler}
                  />
                </Form.Group>
              </Col>
            </>
          )}
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Inkommen</Form.Label>
              <Form.Control
                type="date"
                value={orderData.arriveDate ?? ""}
                onInput={arriveDateInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Bokad (prod.dag)</Form.Label>
              <Form.Control
                type="date"
                value={orderData.bookingDate ?? ""}
                onInput={bookingDateInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Leveransdatum</Form.Label>
              <Form.Control
                type="date"
                value={orderData.deliveryDate ?? ""}
                onInput={deliveryDateInputHandler}
              />
            </Form.Group>
          </Col>

          {clientType === ClientType.Standard && (
            <Col xs={12} md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Filnamn</Form.Label>
                <Form.Control
                  type="text"
                  value={orderData.filename ?? ""}
                  onInput={orderFilenameInputHandler}
                />
              </Form.Group>
            </Col>
          )}

          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Kommentar</Form.Label>
              <Form.Control
                value={orderData.comment ?? ""}
                as="textarea"
                onInput={commentInputHandler}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Intern kommentar</Form.Label>
              <Form.Control
                as="textarea"
                value={orderData.internalComment ?? ""}
                onInput={internalCommentInputHandler}
              />
            </Form.Group>
          </Col>
        </Row>
        {clientType === ClientType.Fleet && (
          <Row>
            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meddelande på prisförslag</Form.Label>
                <Form.Control
                  as="textarea"
                  value={orderData.priceProposalMessage ?? ""}
                  onInput={priceProposalMessageInputHandler}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
        {(clientType === ClientType.Catalog || clientType === ClientType.Fleet) && (
          <>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Godkänd av kund"
                    className="mt-2"
                    checked={orderData.approved || false}
                    onChange={orderApprovedInputHandler}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
        {clientType === ClientType.Fleet && (
          <>
            <Row>
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    label="Nekad av kund"
                    className="mt-2"
                    checked={orderData.denied || false}
                    onChange={orderDeniedInputHandler}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {clientType === ClientType.Fleet && (
          <>
            <Row>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ordernummer hos Postnord</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.clientExternalOrderId ?? ""}
                    onInput={clientExternalOrderIdInputHandler}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Miltal</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.odometer ?? ""}
                    onInput={odometerInputHandler}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Hämtad av</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.clientVehicleRetriever ?? ""}
                    onInput={clientVehicleRetrieverInputHandler}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Verkstadens id</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.workshopId ?? ""}
                    onInput={workshopIdInputHandler}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Verkstadsnamn</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderData.workshopName ?? ""}
                    onInput={workshopNameInputHandler}
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}
        <h3>Artiklar</h3>
        <Col xs={12} md={12}>
          <Table responsive striped bordered hover>
            <thead>
              <tr key="-1">
                <th style={{ width: "5%" }}>
                  {allVerified ? (
                    <CheckAll className="text-success" />
                  ) : (
                    <ExclamationTriangle className="text-danger" />
                  )}
                </th>
                {clientType === ClientType.Fleet && (
                  <th>Välj mall</th>
                )}
                <th>Art.nr</th>
                <th>Beskrivning</th>
                <th>Antal</th>
                <th>Försäljningspris</th>
                {(clientType === ClientType.Standard || clientType === ClientType.Fleet) && (
                  <th>Rabatt (%)</th>
                )}
                {clientType === ClientType.Fleet && (
                  <th>Exkluderad</th>
                )}
                <th>
                  <Pen />
                </th>
              </tr>
            </thead>
            <tbody>
              {orderData?.articles
                ?.filter((article) => article.articleType === ArticleType.Product)
                .map((article) => (
                  <tr key={article.rowNumber}>
                    <td>
                      {!article.verified ? (
                        <X className="text-danger" />
                      ) : (
                        <Check className="text-success" />
                      )}
                    </td>
                    {clientType === ClientType.Fleet && (
                      <td>
                        <Form.Select
                          size="sm"
                          className="mt-1"
                          value={article.articleNumber || ""}
                          onChange={(e) => {
                            const selectedTemplate = articleTemplateListProduct.find(t => t.articleNumber === e.target.value);
                            if (selectedTemplate) {
                              articleTemplateHandler(article.rowNumber, selectedTemplate.id, ArticleType.Product);
                            }
                          }}
                        >
                          <option value="">- Välj mall -</option>
                          {articleTemplateListProduct && articleTemplateListProduct.map((template) => (
                            <option key={template.id} value={template.articleNumber}>
                              {template.articleNumber} - {template.description}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    )}
                    <td>
                      <Form.Control
                        type="text"
                        value={article.articleNumber}
                        size="sm"
                        onChange={(e) =>
                          articleListArticleNumberHandler(
                            article.rowNumber,
                            e.target.value
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        value={article.description}
                        size="sm"
                        onChange={(e) =>
                          articleListDescriptionInputHandler(
                            article.rowNumber,
                            e.target.value
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        value={article.quantity}
                        size="sm"
                        onChange={(e) =>
                          articleListQuantityHandler(
                            article.rowNumber,
                            parseInt(e.target.value)
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    <td>
                      <Form.Control
                        type="number"
                        defaultValue={article.salesPrice ?? 0}
                        value={article.salesPrice}
                        size="sm"
                        onChange={(e) =>
                          articleListSalesPriceHandler(
                            article.rowNumber,
                            e.target.value
                          )
                        }
                        readOnly={orderData.exported || article.verified}
                        disabled={orderData.exported || article.verified}
                      ></Form.Control>
                    </td>
                    {(clientType === ClientType.Standard || clientType === ClientType.Fleet) && (
                      <td>
                        <Form.Control
                          type="text"
                          defaultValue={article.discount ?? 0}
                          value={article.discount ?? ""}
                          size="sm"
                          onChange={(e) =>
                            articleListDiscountHandler(
                              article.rowNumber,
                              e.target.value
                            )
                          }
                          readOnly={article.verified}
                          disabled={article.verified}
                        ></Form.Control>
                      </td>
                    )}
                    {clientType === ClientType.Fleet && (
                      <td align="center">
                        <Form.Check
                          type="switch"
                          checked={article.excludeFromWarehousePicking || false}
                          onChange={(e) => {
                            setOrderData({
                              ...orderData,
                              articles: orderData.articles!.map(a =>
                                a.rowNumber === article.rowNumber
                                  ? { ...a, excludeFromWarehousePicking: e.target.checked }
                                  : a
                              ),
                            });
                          }}
                        />
                      </td>
                    )}
                    <td
                      onClick={() => openArticleModalHandler(article)}
                      role="button"
                    >
                      <Pen />
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Col>

        <Modal
          size="xl"
          show={showArticleModal}
          onHide={() => closeArticleModalHandler()}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {modalArticle.articleNumber
                ? `${modalArticle.articleNumber} - ${modalArticle.description}`
                : "Ny artikel"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col xs={12} md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Antal beställda</Form.Label>
                    <Form.Control
                      type="number"
                      defaultValue={modalArticle.quantity ?? ""}
                      onInput={articleQuantityInputHandler}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Antal levererade</Form.Label>
                    <Form.Control
                      type="number"
                      defaultValue={modalArticle.deliveredQuantity ?? ""}
                      onInput={articleDeliveredQuantityInputHandler}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Beskrivning</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.description ?? ""}
                      onInput={articleDescriptionInputHandler}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Leverantör</Form.Label>
                    <Form.Select
                      aria-label="Leverantör"
                      value={modalArticle.supplierId || undefined}
                      onChange={(e) =>
                        articleSupplierInputHandler(e.target.value)
                      }
                    >
                      <option key={0} value="">
                        - Ingen leverantör vald -
                      </option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Artikelnummer</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.articleNumber ?? ""}
                      onInput={articleArticleNumberInputHandler}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Externt kommentar</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.comment}
                      onInput={articleCommentInputHandler}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Intern kommentar</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.internalComment ?? ""}
                      onInput={articleInternalCommentInputHandler}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Externt prefix</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.externalArticleCode ?? ""}
                      onInput={articleExternalArticleCodeInputHandler}
                    />
                  </Form.Group>
                </Col>
                {(clientType === ClientType.Standard || clientType === ClientType.Fleet) && (
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Rabatt (%)</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        max={100}
                        value={modalArticle.discount ?? ""}
                        onChange={articleDiscountInputHandler}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>IO-nummer</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.purchaseOrderNumber ?? ""}
                      onInput={articlePurchaseOrderNumberInputHandler}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Inköpspris</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.purchasePrice ?? 0}
                      value={modalArticle.purchasePrice}
                      onInput={articlePurchasePriceInputHandler}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Försäljningspris</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.salesPrice ?? 0}
                      value={modalArticle.salesPrice}
                      onInput={articleSalesPriceInputHandler}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SPN</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.storeSalesPrice ?? 0}
                      value={modalArticle.storeSalesPrice}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>VSN</Form.Label>
                    <Form.Control
                      type="text"
                      defaultValue={modalArticle.workshopSalesPrice ?? 0}
                      value={modalArticle.workshopSalesPrice}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
              <hr />
              <Row>
                <Col xs={6} md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Totalt exkl. moms</Form.Label>
                    <Form.Control
                      type="text"
                      value={modalArticle.totalExVat ?? ""}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Totalt inkl. moms</Form.Label>
                    <Form.Control
                      type="text"
                      value={modalArticle.totalIncVat ?? ""}
                      readOnly
                      disabled
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Container>
              {!orderData.exported && (
                <div className="float-start">
                  <Button
                    className="mb-3"
                    variant="secondary"
                    onClick={removeArticleHandler}
                  >
                    Ta bort artikel
                  </Button>
                </div>
              )}

              <div className="float-end d-inline-flex">
                <div className="me-3">
                  <Form.Check
                    type="switch"
                    label="Granskad offert"
                    className="mt-2"
                    onChange={articleReviewedQuoteInputHandler}
                    checked={modalArticle.reviewedQuote}
                  />
                </div>
                <div className="me-3">
                  <Form.Check
                    type="switch"
                    label="Klarmarkerad"
                    className="mt-2"
                    onChange={validatedHandler}
                    checked={modalArticle.verified}
                  />
                </div>
                <div>
                  <Button
                    className="ms-3 mb-3"
                    variant="primary"
                    onClick={saveArticleInModalHandler}
                  >
                    Spara ändringar
                  </Button>
                </div>
              </div>
            </Container>
          </Modal.Footer>
        </Modal>
        {!orderData.exported && (
          <Button
            variant="outline-primary"
            className="mb-3"
            onClick={addNewArticleHandler}
          >
            Lägg till artikel <Plus />
          </Button>
        )}

        {clientType === ClientType.Fleet && (
          <Col xs={12} md={12} className="mb-3">
            <>
              <h3>Reparationstider</h3>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>Art.nr</th>
                    <th>Beskrivning</th>
                    <th>Antal</th>
                    <th>Försäljningspris</th>
                    <th>Tid</th>
                  </tr>
                </thead>
                <tbody>
                  {orderData.articles &&
                    orderData.articles
                      .filter((article) => article.articleType === ArticleType.Time)
                      .map((article) => (
                        <tr key={article.rowNumber}>
                          <td>
                            <Form.Select
                              size="sm"
                              className="mt-1"
                              value={article.articleNumber || ""}
                              onChange={(e) => {
                                const selectedTemplate = articleTemplateListTime.find(t => t.articleNumber === e.target.value);
                                if (selectedTemplate) {
                                  articleTemplateHandler(article.rowNumber, selectedTemplate.id, ArticleType.Time);
                                }
                              }}
                            >
                              <option value="">- Välj mall -</option>
                              {articleTemplateListTime && articleTemplateListTime.map((template) => (
                                <option key={template.id} value={template.articleNumber}>
                                  {template.articleNumber} - {template.description}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              type="text"
                              value={article.description}
                              size="sm"
                              onChange={(e) =>
                                articleListDescriptionInputHandler(
                                  article.rowNumber,
                                  e.target.value
                                )
                              }
                              readOnly={orderData.exported || article.verified}
                              disabled={orderData.exported || article.verified}
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.quantity}
                              size="sm"
                              onChange={(e) =>
                                articleListQuantityHandler(
                                  article.rowNumber,
                                  parseInt(e.target.value)
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.salesPrice}
                              size="sm"
                              readOnly
                              disabled
                            />
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              value={article.time}
                              size="sm"
                              onChange={(e) =>
                                articleListTimeHandler(
                                  article.rowNumber,
                                  parseInt(e.target.value)
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))}
                </tbody>
              </Table>
            </>
            <Button
              variant="outline-primary"
              className="mb-3"
              onClick={addNewTimeArticleHandler}
            >
              Lägg till tid <Plus />
            </Button>
          </Col>

        )}

        <hr />

        <div className="text-end">
          <Button
            variant="primary"
            className="mb-3"
            onClick={handleAddOrder}
            disabled={isWaitingOrderPostResponse}
          >
            Lägg till order
          </Button>
        </div>

        <Alert key="alert" variant="danger" show={displayError}>
          <p>
            {orderData.customerId === ""
              ? "Du måste välja en kund innan du kan spara ordern."
              : "Något gick fel, kontrollera uppgifterna och försök igen."}
          </p>
        </Alert>
      </Form>
    </div>
  );
};

export default OrderAdd;
